
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Heading,
  Row,
  Stack,
  Text,
  Time,
  type BadgeTone,
} from '@client/shared/design';
import { useLocation } from '@client/shared/router/router.tsx';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { DEAD_SUBSCRIPTION_STATUSES } from '@shared/billing';
import {
  createCheckoutUrl,
  createPortalUrl,
  redirectToStripe,
  type BillingInterval,
  type BillingStatus,
} from './billing-operations.ts';
import { trialDaysLeft, useBillingStatus } from './use-billing.ts';
import styles from './billing.module.css';

// Display copy only — the amounts Stripe actually charges come from the catalog prices
// behind the standard_monthly / standard_yearly lookup keys.
const PLANS: readonly {
  interval: BillingInterval;
  label: string;
  price: string;
  per: string;
  note?: string;
}[] = [
  { interval: 'monthly', label: 'Monthly', price: '$199', per: 'per month' },
  { interval: 'yearly', label: 'Yearly', price: '$1,990', per: 'per year', note: '2 months free' },
];

type Standing = 'trial' | 'active' | 'past_due' | 'free';

function standingOf(status: BillingStatus): Standing {
  if (status.subscriptionStatus === 'active' || status.subscriptionStatus === 'trialing') {
    return 'active';
  }
  if (status.subscriptionStatus === 'past_due') return 'past_due';
  return status.inGoodStanding ? 'trial' : 'free';
}

const STANDING_BADGE: Record<Standing, { tone: BadgeTone; label: string }> = {
  trial: { tone: 'accent', label: 'Free trial' },
  active: { tone: 'ok', label: 'Standard' },
  past_due: { tone: 'warn', label: 'Past due' },
  free: { tone: 'neutral', label: 'Free plan' },
};

export function BillingSection() {
  const params = new URLSearchParams(useLocation().search);
  const checkoutFlag = params.get('billing');

  const [redirectError, setRedirectError] = useState<string | null>(null);

  const query = useBillingStatus({
    // Back from a successful Checkout the webhook may not have landed yet; poll until the
    // mirrored status shows up.
    refetchInterval:
      checkoutFlag === 'success'
        ? (q: { state: { data?: BillingStatus } }) =>
            q.state.data?.subscriptionStatus ? false : 2000
        : false,
  });

  const checkout = useMutation({
    mutationFn: createCheckoutUrl,
    onSuccess: (url) => redirectToStripe(url),
    onError: (err) => setRedirectError(messageFor(err, 'Couldn’t open Stripe Checkout. Try again.')),
  });
  const portal = useMutation({
    mutationFn: createPortalUrl,
    onSuccess: (url) => redirectToStripe(url),
    onError: (err) =>
      setRedirectError(messageFor(err, 'Couldn’t open the billing portal. Try again.')),
  });

  const status = query.data;

  if (status && !status.enabled) return null;

  return (
    <Card pad="lg" spine as="section" aria-labelledby="billing-heading">
      <Stack gap={5}>
        <Row gap={3} align="center">
          <Stack gap={1} className={styles.sectionIntro}>
            <Heading level={3} as="h2" id="billing-heading">
              Billing
            </Heading>
            <Text size="sm" tone="soft">
              Your plan, payment, and listing allowance.
            </Text>
          </Stack>
          {status && (
            <Badge tone={STANDING_BADGE[standingOf(status)].tone} dot>
              {STANDING_BADGE[standingOf(status)].label}
            </Badge>
          )}
        </Row>

        {checkoutFlag === 'success' && (
          <p className={styles.flash} data-tone="ok" role="status">
            {status?.subscriptionStatus
              ? 'You’re subscribed. Payment is set up for your group.'
              : 'Payment is set up. Confirming with Stripe…'}
          </p>
        )}
        {checkoutFlag === 'cancelled' && (
          <p className={styles.flash} data-tone="neutral" role="status">
            Checkout was cancelled, and nothing changed.
          </p>
        )}

        {query.isPending && (
          <Text size="sm" tone="soft">
            Loading billing status…
          </Text>
        )}
        {query.isError && (
          <Text size="sm" tone="danger" role="alert">
            {messageFor(query.error, 'Couldn’t load billing status. Refresh to try again.')}
          </Text>
        )}

        {status && (
          <BillingBody
            status={status}
            onSubscribe={(interval) => {
              setRedirectError(null);
              checkout.mutate(interval);
            }}
            onManage={() => {
              setRedirectError(null);
              portal.mutate();
            }}
            subscribing={checkout.isPending ? (checkout.variables ?? null) : null}
            managing={portal.isPending}
          />
        )}

        {redirectError && (
          <Text size="sm" tone="danger" role="alert">
            {redirectError}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

function BillingBody({
  status,
  onSubscribe,
  onManage,
  subscribing,
  managing,
}: {
  status: BillingStatus;
  onSubscribe: (interval: BillingInterval) => void;
  onManage: () => void;
  subscribing: BillingInterval | null;
  managing: boolean;
}) {
  const standing = standingOf(status);
  // Dead statuses are ones the Customer Portal can't revive — offer the plans again
  // instead. Shared with the server's already-subscribed guard so the two can't drift.
  const showPlans =
    status.subscriptionStatus == null ||
    DEAD_SUBSCRIPTION_STATUSES.includes(status.subscriptionStatus);
  const showManage = status.subscriptionStatus != null;
  const days = trialDaysLeft(status);

  return (
    <Stack gap={5}>
      {standing === 'trial' && (
        <Text size="sm" tone="soft">
          Your group is on the free trial of Cheddule Standard, with everything included and no
          card on file. It ends <Time at={status.trialEndsAt} date />{' '}
          <strong className={styles.daysLeft}>
            ({days} {days === 1 ? 'day' : 'days'} left)
          </strong>
          .{' '}
          {status.postTrialCap < status.maxActiveListings && (
            <>
              After that your group keeps up to {status.postTrialCap} active{' '}
              {status.postTrialCap === 1 ? 'listing' : 'listings'}; subscribe below to keep the
              full allowance.
            </>
          )}
        </Text>
      )}
      {standing === 'active' && (
        <Text size="sm" tone="soft">
          Cheddule Standard is active for your group. Change plans, update the card, or download
          invoices from the billing portal.
        </Text>
      )}
      {standing === 'past_due' && (
        <Text size="sm" tone="soft">
          The last payment didn’t go through. Stripe is retrying automatically; update your card in
          the billing portal to settle it.
        </Text>
      )}
      {standing === 'free' && (
        <Text size="sm" tone="soft">
          Your group is on the Free plan: up to {status.maxActiveListings} active{' '}
          {status.maxActiveListings === 1 ? 'listing' : 'listings'} with unlimited agents.
          Subscribe to Standard below for the full allowance.
        </Text>
      )}

      {showPlans && (
        <div className={styles.plans}>
          {PLANS.map((plan) => (
            <div key={plan.interval} className={styles.plan}>
              <Row gap={2} align="center">
                <Text size="xs" weight="semibold" className={styles.planLabel}>
                  {plan.label}
                </Text>
                {plan.note && <Badge tone="ok">{plan.note}</Badge>}
              </Row>
              <p className={styles.planPrice}>
                {plan.price} <span className={styles.planPer}>{plan.per}</span>
              </p>
              <Button
                variant={plan.interval === 'yearly' ? 'primary' : 'secondary'}
                onClick={() => onSubscribe(plan.interval)}
                loading={subscribing === plan.interval}
                disabled={subscribing != null && subscribing !== plan.interval}
              >
                Subscribe {plan.interval}
              </Button>
            </div>
          ))}
        </div>
      )}

      {showManage && (
        <Row gap={3}>
          <Button variant="secondary" onClick={onManage} loading={managing}>
            Manage billing
          </Button>
        </Row>
      )}

      <ListingAllowance status={status} />
    </Stack>
  );
}

// Past this, one-cell-per-slot degrades into hairlines (enterprise overrides go to 1000);
// the numeric count above the meter carries the information on its own.
const METER_MAX_CELLS = 30;

function ListingAllowance({ status }: { status: BillingStatus }) {
  const cells = Array.from({ length: status.maxActiveListings }, (_, i) => i < status.activeListings);
  return (
    <Stack gap={2}>
      <Row gap={2} align="baseline">
        <Text size="xs" weight="semibold" className={styles.planLabel}>
          Listings
        </Text>
        <Text size="sm" tone="soft" className={styles.meterCount}>
          {status.activeListings} of {status.maxActiveListings} active
        </Text>
      </Row>
      {status.maxActiveListings <= METER_MAX_CELLS && (
        <div
          className={styles.meter}
          role="img"
          aria-label={`${status.activeListings} of ${status.maxActiveListings} listing slots in use`}
        >
          {cells.map((filled, i) => (
            <span key={i} className={styles.meterCell} data-filled={filled || undefined} />
          ))}
        </div>
      )}
      {status.activeListings > status.maxActiveListings ? (
        <Text size="xs" tone="soft">
          You have more active listings than your plan allows. They all keep working; archive
          some or subscribe to add new ones.
        </Text>
      ) : status.activeListings >= status.maxActiveListings ? (
        <Text size="xs" tone="soft">
          All slots are in use. Archive a listing to make room for a new one.
        </Text>
      ) : null}
    </Stack>
  );
}
